import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, FlaskConical, RefreshCw, TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { FREQUENT_SYNC_PROVIDERS, needsFrequentSync, PROVIDER_FEATURE, TRIAL_BLOCKED_FREQUENCIES, usePlan } from '../lib/billing';
import { useDesk } from '../lib/desk';
import { formatDateTime, formatRelative, humanizeKey, SYNC_FREQUENCY_LABEL } from '../lib/format';
import { formatTimeOfDay, frequencyOptions, isMailbox, PROVIDER_LABEL, providerPath, syncs, syncTimeOptions, timeZoneName } from '../lib/integrations';
import type { Integration, IntegrationProvider, SyncFrequency, SyncRun } from '../lib/types';
import { IntegrationStatusBadge, SyncStatusBadge } from './badges';
import { AddOnNotice } from './plan';
import { useToast } from './toast';
import { Alert, Badge, Button, Card, cn, EmptyState, Field, Input, Modal, Select, Spinner, Switch, Table, Tabs, Td, Th } from './ui';

const SYNC_WORDING = {
  now: 'Sync now',
  running: 'Syncing…',
  started: 'Sync started',
  alreadyRunning: 'A sync is already in progress',
  enabled: 'Allow scheduled and manual syncs',
  last: 'Last successful sync',
  next: 'Next scheduled sync',
  details: 'Sync details',
  history: 'Sync history',
  emptyTitle: 'No syncs yet',
  emptyText: 'Run a sync to see its results here.',
  failed: 'The last sync or test failed',
};

// Mailboxes aren't synced, they're checked for new mail.
const CHECK_WORDING: typeof SYNC_WORDING = {
  now: 'Check now',
  running: 'Checking…',
  started: 'Checking the mailbox',
  alreadyRunning: 'A check is already in progress',
  enabled: 'Turn new emails into tickets on the schedule',
  last: 'Last successful check',
  next: 'Next check',
  details: 'Mailbox check',
  history: 'Check history',
  emptyTitle: 'No checks yet',
  emptyText: 'Check the mailbox to see the results here.',
  failed: 'The last check or test failed',
};

// Billing Management doesn't sync: it's on or off, and posts when an administrator asks.
const POST_WORDING: typeof SYNC_WORDING = {
  ...SYNC_WORDING,
  enabled: 'Let administrators post damage invoices to Blackbaud',
  failed: 'The last connection test failed',
};

const wordingFor = (provider: IntegrationProvider) => (isMailbox(provider) ? CHECK_WORDING : syncs(provider) ? SYNC_WORDING : POST_WORDING);

/** A fact in the strip under an integration's name, for integrations that don't sync. */
export interface IntegrationFact {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
}

export function useIntegrationMutations(provider: IntegrationProvider) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const wording = wordingFor(provider);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    void queryClient.invalidateQueries({ queryKey: ['managed-devices'] });
  };

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put<Integration>(`/integrations/${providerPath(provider)}`, body),
    onSuccess: () => {
      refresh();
      toast.success('Saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const sync = useMutation({
    mutationFn: () => api.post<{ alreadyQueued: boolean }>(`/integrations/${providerPath(provider)}/sync`),
    onSuccess: (result) => {
      refresh();
      toast.success(result.alreadyQueued ? wording.alreadyRunning : wording.started);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const test = useMutation({
    mutationFn: () => api.post<{ ok: boolean; message: string }>(`/integrations/${providerPath(provider)}/test`),
    onSuccess: (result) => {
      refresh();
      if (result.ok) toast.success(result.message);
      else toast.error(result.message);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return { update, sync, test, refresh };
}

export function CopyField({ label, value, hint }: { label: string; value: string; hint?: ReactNode }) {
  const toast = useToast();
  return (
    <Field label={label} hint={hint}>
      <div className="flex gap-2">
        <Input readOnly value={value} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
        <Button
          icon={<Copy />}
          onClick={async () => {
            await navigator.clipboard.writeText(value);
            toast.success('Copied');
          }}
        >
          Copy
        </Button>
      </div>
    </Field>
  );
}

/** How far a running sync has got: its phase, a count, and a bar. */
export function SyncProgressMeter({ run }: { run: SyncRun }) {
  const progress = run.progress;
  const percent = run.status === 'QUEUED' ? 0 : Math.max(2, progress?.percent ?? 0);
  const label = run.status === 'QUEUED' ? 'Waiting for the sync worker to start' : (progress?.label ?? 'Starting');
  return (
    <div className="space-y-1.5" role="status" aria-live="polite">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-xs">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-500 tabular-nums">
          {progress && run.status === 'RUNNING' && progress.done > 0
            ? `${progress.done.toLocaleString()}${progress.total ? ` of ${progress.total.toLocaleString()}` : ''} · `
            : ''}
          {run.status === 'RUNNING' ? `${percent}%` : ''}
        </span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-slate-200"
        role="progressbar"
        aria-label="Sync progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <div className={cn('h-full rounded-full bg-brand-600 transition-[width] duration-700', run.status === 'QUEUED' && 'w-1/3 animate-pulse bg-slate-300')} style={run.status === 'QUEUED' ? undefined : { width: `${percent}%` }} />
      </div>
    </div>
  );
}

function SyncRunModal({ runId, onClose }: { runId: string; onClose(): void }) {
  const run = useQuery({
    queryKey: ['integrations', 'run', runId],
    queryFn: () => api.get<SyncRun>(`/sync-runs/${runId}`),
    refetchInterval: (q) => (q.state.data && ['QUEUED', 'RUNNING'].includes(q.state.data.status) ? 2000 : false),
  });
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={run.data ? wordingFor(run.data.provider).details : 'Details'}
      description={run.data && `${PROVIDER_LABEL[run.data.provider]} · ${formatDateTime(run.data.createdAt)}`}
    >
      {!run.data ? (
        <Spinner />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <SyncStatusBadge status={run.data.status} />
            <Badge>{run.data.trigger === 'MANUAL' ? 'Manual' : 'Scheduled'}</Badge>
          </div>
          {(run.data.status === 'RUNNING' || run.data.status === 'QUEUED') && run.data.progress !== undefined && <SyncProgressMeter run={run.data} />}
          {run.data.error && <Alert tone="red">{run.data.error}</Alert>}
          {Object.keys(run.data.stats ?? {}).length > 0 && (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {Object.entries(run.data.stats).map(([key, value]) => (
                <div key={key} className="rounded-lg border border-slate-200 px-3 py-2">
                  <p className="text-lg font-semibold">{value.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{humanizeKey(key)}</p>
                </div>
              ))}
            </div>
          )}
          <div className="max-h-80 overflow-auto rounded-lg bg-slate-950 p-3 font-mono text-xs leading-relaxed text-slate-200">
            {(run.data.log ?? []).length === 0 ? (
              <p className="text-slate-500">No log output yet.</p>
            ) : (
              run.data.log!.map((line, index) => (
                <p key={index} className={line.level === 'error' ? 'text-red-400' : line.level === 'warn' ? 'text-amber-300' : undefined}>
                  <span className="text-slate-500">{new Date(line.at).toLocaleTimeString()}</span> {line.message}
                </p>
              ))
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function SyncHistory({ runs, wording }: { runs: SyncRun[]; wording: typeof SYNC_WORDING }) {
  const [openRun, setOpenRun] = useState<string | null>(null);
  if (runs.length === 0) return <EmptyState title={wording.emptyTitle} description={wording.emptyText} />;
  return (
    <>
      <Table>
        <thead>
          <tr>
            <Th>Started</Th>
            <Th>Status</Th>
            <Th>Trigger</Th>
            <Th>Result</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {runs.map((run) => (
            <tr key={run.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenRun(run.id)}>
              <Td className="whitespace-nowrap">{formatDateTime(run.startedAt ?? run.createdAt)}</Td>
              <Td>
                <SyncStatusBadge status={run.status} />
              </Td>
              <Td className="text-slate-500">{run.trigger === 'MANUAL' ? 'Manual' : 'Scheduled'}</Td>
              <Td className="max-w-md truncate text-xs text-slate-600">
                {run.error ??
                  (Object.entries(run.stats ?? {})
                    .filter(([, v]) => v > 0)
                    .map(([k, v]) => `${v.toLocaleString()} ${humanizeKey(k).toLowerCase()}`)
                    .join(' · ') ||
                    '—')}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {openRun && <SyncRunModal runId={openRun} onClose={() => setOpenRun(null)} />}
    </>
  );
}

export function IntegrationShell({
  integration,
  title,
  description,
  icon,
  tabs,
  syncAllowed = true,
  facts = [],
}: {
  integration: Integration;
  title: string;
  description: string;
  icon: ReactNode;
  tabs: { value: string; label: string; content: ReactNode }[];
  /** Technicians may sync, unless the integration limits who can. */
  syncAllowed?: boolean;
  /** Shown instead of the schedule, for integrations that don't sync. */
  facts?: IntegrationFact[];
}) {
  const { can } = useDesk();
  const { me } = useAuth();
  const isAdmin = can('ADMIN');
  const { update, sync, test } = useIntegrationMutations(integration.provider);
  const [tab, setTab] = useState(tabs[0].value);
  const daily = integration.syncFrequency === 'DAILY';
  const scheduled = integration.enabled && integration.syncFrequency !== 'MANUAL' && integration.nextSyncAt;
  const timezone = me?.tenant?.timezone;
  const wording = wordingFor(integration.provider);
  const running = integration.recentRuns.find((r) => r.status === 'QUEUED' || r.status === 'RUNNING');
  const syncing = syncs(integration.provider);
  const allTabs = syncing ? [...tabs, { value: 'history', label: wording.history, content: <SyncHistory runs={integration.recentRuns} wording={wording} /> }] : tabs;
  // Add-on integrations, and the hourly and 6-hour schedules that need Frequent Integration Sync.
  const plan = usePlan();
  const feature = PROVIDER_FEATURE[integration.provider];
  const withoutAddOn = !!feature && !plan.has(feature);
  const frequencyLocked = (frequency: SyncFrequency) => needsFrequentSync(integration.provider, frequency) && !plan.has('FREQUENT_SYNC');
  const frequencies = frequencyOptions(integration.provider, integration.syncFrequency);
  // During a sign-up trial, rosters and devices sync on demand; mailboxes are still checked on schedule.
  const waitsForPlan = !plan.scheduledSyncs && FREQUENT_SYNC_PROVIDERS.includes(integration.provider);
  const trialLocked = (frequency: SyncFrequency) => waitsForPlan && TRIAL_BLOCKED_FREQUENCIES.includes(frequency);

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-5">
        <div className="flex items-start gap-4">
          <div className="shrink-0">{icon}</div>
          <div>
            <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-slate-900">
              {title}
              <IntegrationStatusBadge status={integration.status} enabled={integration.enabled} />
              {integration.mode === 'DEMO' && <Badge tone="violet">Demo data</Badge>}
            </h2>
            <p className="mt-0.5 max-w-2xl text-sm text-slate-500">{description}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <Button icon={<FlaskConical />} loading={test.isPending} onClick={() => test.mutate()}>
              Test connection
            </Button>
          )}
          {can('TECHNICIAN') && syncAllowed && syncing && (
            <Button
              variant="primary"
              icon={<RefreshCw />}
              loading={sync.isPending || !!running}
              disabled={!integration.enabled || withoutAddOn}
              onClick={() => sync.mutate()}
            >
              {running ? wording.running : wording.now}
            </Button>
          )}
        </div>
      </div>

      {feature && (
        <AddOnNotice feature={feature} className="mx-5 mb-4">
          {integration.enabled ? 'It stays set up, but doesn’t run until it’s on your plan.' : 'Turn it on once it’s on your plan.'}
        </AddOnNotice>
      )}

      <div className="grid gap-4 border-y border-slate-100 bg-slate-50/60 px-5 py-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Switch
            label="Enabled"
            description={wording.enabled}
            checked={integration.enabled}
            disabled={!isAdmin || update.isPending || (withoutAddOn && !integration.enabled)}
            onChange={(enabled) => update.mutate({ enabled })}
          />
        </div>
        {!syncing &&
          facts.map((fact) => (
            <div key={fact.label}>
              <p className="text-xs font-medium text-slate-500">{fact.label}</p>
              <p className="mt-1 font-medium text-slate-900">{fact.value}</p>
              {fact.detail && <p className="text-xs text-slate-500">{fact.detail}</p>}
            </div>
          ))}
        {syncing && (
          <>
            <div>
              <p className="text-xs font-medium text-slate-500">Schedule</p>
              <Select
                aria-label="Schedule"
                className="mt-1"
                value={integration.syncFrequency}
                disabled={!isAdmin}
                onChange={(e) => update.mutate({ syncFrequency: e.target.value as SyncFrequency })}
              >
                {frequencies.map((f) => (
                  <option key={f} value={f} disabled={f !== integration.syncFrequency && (trialLocked(f) || frequencyLocked(f))}>
                    {SYNC_FREQUENCY_LABEL[f]}
                    {trialLocked(f) ? ' (after you subscribe)' : frequencyLocked(f) && ' (Frequent Integration Sync)'}
                  </option>
                ))}
              </Select>
              {daily && (
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Select
                    aria-label="Time of day"
                    className="w-32"
                    value={integration.syncTime}
                    disabled={!isAdmin}
                    onChange={(e) => update.mutate({ syncTime: e.target.value })}
                  >
                    {syncTimeOptions(integration.syncTime).map((time) => (
                      <option key={time} value={time}>
                        {formatTimeOfDay(time)}
                      </option>
                    ))}
                  </Select>
                  {timezone && <span className="text-xs text-slate-500">{timeZoneName(timezone)}</span>}
                </div>
              )}
              {waitsForPlan && (
                <p className="mt-2 text-xs text-slate-500">
                  During your free trial, this integration syncs when you click {wording.now}. Scheduled syncs open up once you{' '}
                  {isAdmin ? (
                    <Link to="/billing" className="font-medium text-brand-700 hover:underline">
                      subscribe
                    </Link>
                  ) : (
                    'subscribe'
                  )}
                  .
                </p>
              )}
              {isAdmin && plan.enforced && !waitsForPlan && frequencies.some(frequencyLocked) && (
                <p className="mt-2 text-xs text-slate-500">
                  {isMailbox(integration.provider) ? 'Checking it every minute needs the' : 'Syncing every hour or every 6 hours needs the'}{' '}
                  <Link to="/billing" className="font-medium text-brand-700 hover:underline">
                    Frequent Integration Sync
                  </Link>{' '}
                  add-on.
                </p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">{wording.last}</p>
              <p className="mt-1 font-medium text-slate-900">{integration.lastSuccessAt ? formatRelative(integration.lastSuccessAt) : 'Never'}</p>
              {integration.lastSuccessAt && <p className="text-xs text-slate-500">{formatDateTime(integration.lastSuccessAt)}</p>}
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">{wording.next}</p>
              <p className="mt-1 font-medium text-slate-900">{waitsForPlan && scheduled ? 'After you subscribe' : scheduled ? formatRelative(integration.nextSyncAt) : '—'}</p>
              {scheduled && !waitsForPlan && <p className="text-xs text-slate-500">{formatDateTime(integration.nextSyncAt)}</p>}
            </div>
          </>
        )}
      </div>

      {running && (running.progress || integration.provider === 'ONEROSTER') && (
        <div className="border-b border-slate-100 px-5 py-4">
          <SyncProgressMeter run={running} />
          <p className="mt-2 text-xs text-slate-500">The sync runs in the background. You can leave this page and come back.</p>
        </div>
      )}

      {integration.lastError && (
        <div className="px-5 pt-4">
          <Alert tone="red" icon={<TriangleAlert />} title={wording.failed}>
            {integration.lastError}
          </Alert>
        </div>
      )}

      <div className="px-5 pt-3">
        <Tabs value={tab} onChange={setTab} tabs={allTabs.map(({ value, label }) => ({ value, label }))} />
      </div>
      <div className={cn(tab === 'history' ? 'pt-1' : 'p-5')}>{allTabs.find((t) => t.value === tab)?.content}</div>
    </Card>
  );
}

export function DataSourcePicker({ integration }: { integration: Integration }) {
  const { can } = useDesk();
  const { update } = useIntegrationMutations(integration.provider);
  const options =
    isMailbox(integration.provider)
      ? [
          {
            value: 'LIVE',
            title: 'Live mailbox',
            text: integration.provider === 'FACILITIES_EMAIL' ? 'Turn email sent to your facilities address into requests.' : 'Turn email sent to your help desk address into tickets.',
          },
          { value: 'DEMO', title: 'Sample emails', text: 'A few sample emails become tickets, to try it out. No mailbox needed.' },
        ]
      : [
          { value: 'LIVE', title: 'Live connection', text: 'Sync real data from your school’s account.' },
          { value: 'DEMO', title: 'Demo data', text: 'Realistic sample data for evaluation and training. No credentials needed.' },
        ];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={!can('ADMIN') || update.isPending}
          onClick={() => option.value !== integration.mode && update.mutate({ mode: option.value })}
          className={cn(
            'rounded-lg border px-4 py-3 text-left transition-colors disabled:cursor-not-allowed',
            integration.mode === option.value ? 'border-brand-500 bg-brand-50/60 ring-1 ring-brand-500' : 'border-slate-200 hover:border-slate-300',
          )}
        >
          <p className="text-sm font-medium text-slate-900">{option.title}</p>
          <p className="mt-0.5 text-xs text-slate-500">{option.text}</p>
        </button>
      ))}
    </div>
  );
}
